/**
 * PoliciesIndexAgGridHook
 *
 * AG Grid hook for the Risk Policies list (Risk Policies menu item).
 * Read-only, no per-row action buttons — clicking a row pushes
 * "select_policy" so the server can push_navigate to the policy detail page
 * (the plain table used a client-side JS.navigate, which a grid cell
 * renderer can't build).
 */

// Tailwind classes (not inline hex) so these follow the app's `.theme-dark`
// overrides automatically — matches severity_colour/action_colour in
// policies.ex exactly, so the :show page (still plain HTML) stays in sync.
const SEVERITY_CLASSES = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-yellow-100 text-yellow-700",
  low: "bg-blue-100 text-blue-700",
};
const ACTION_CLASSES = {
  hold: "bg-orange-100 text-orange-700",
  decline: "bg-red-100 text-red-700",
  alert: "bg-yellow-100 text-yellow-700",
  monitor: "bg-blue-100 text-blue-700",
};
const DEFAULT_TONE_CLASSES = "bg-gray-100 text-gray-600";

const badge = (value, map) => {
  if (!value) return "";
  const classes = map[value] || DEFAULT_TONE_CLASSES;
  return `<span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${classes}">${value.toUpperCase()}</span>`;
};

const policyCell = (params) => {
  const { name, description } = params.data || {};
  return `<div class="py-1 leading-tight">
    <div class="text-sm font-medium text-gray-900">${name || ""}</div>
    <div class="max-w-[320px] truncate text-xs text-gray-500">${description || ""}</div>
  </div>`;
};

const rulesCell = (params) => {
  const { active_rules, rule_count } = params.data || {};
  return `<span class="text-sm"><span class="font-semibold text-gray-900">${active_rules}</span><span class="text-gray-400">/${rule_count}</span></span>`;
};

const scoreCell = (params) => `<span class="text-gray-700">+${params.value}</span>`;

const statusCell = (params) => {
  const active = (params.data || {}).active_rules > 0;
  const dotClass = active ? "bg-green-500" : "bg-gray-400";
  const textClass = active ? "text-green-700" : "text-gray-500";
  const label = active ? "Active" : "Paused";
  return `<span class="inline-flex items-center gap-1.5 text-xs ${textClass}">
    <span class="h-1.5 w-1.5 rounded-full ${dotClass}"></span>${label}</span>`;
};

const PoliciesIndexAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const hook = this;

    const columnDefs = [
      { headerName: "Policy", field: "name", pinned: "left", width: 280, cellRenderer: policyCell, filter: "agTextColumnFilter" },
      { headerName: "Rules", field: "active_rules", flex: 0.8, minWidth: 90, cellRenderer: rulesCell },
      { headerName: "Severity", field: "severity", flex: 0.9, minWidth: 110, cellRenderer: (p) => badge(p.value, SEVERITY_CLASSES) },
      { headerName: "Action", field: "action", flex: 0.9, minWidth: 110, cellRenderer: (p) => badge(p.value, ACTION_CLASSES) },
      { headerName: "Score", field: "score_weight", flex: 0.8, minWidth: 90, type: "numericColumn", cellRenderer: scoreCell },
      { headerName: "Triggered (30d)", field: "triggered", flex: 1, minWidth: 130, type: "numericColumn" },
      { headerName: "Status", field: "active_rules", colId: "status", flex: 0.9, minWidth: 110, cellRenderer: statusCell, filter: false },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      defaultColDef: { resizable: true, sortable: true, filter: true, minWidth: 80 },
      animateRows: true,
      rowHeight: 52,
      headerHeight: 40,
      rowClass: "cursor-pointer",
      pagination: true,
      paginationPageSize: 15,
      paginationPageSizeSelector: [15, 30, 50],
      popupParent: document.body,
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
            },
          },
        ],
        defaultToolPanel: "",
      },
      onRowClicked: (e) => {
        if (e.data && e.data.slug) hook.pushEvent("select_policy", { slug: e.data.slug });
      },
      overlayNoRowsTemplate: `<span class="text-sm text-gray-500">No policies configured.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("PoliciesIndexAgGridHook: agGrid not found on window");
    }
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        this.gridApi.setGridOption("rowData", JSON.parse(this.el.dataset.rows));
      } catch (e) {
        console.warn("PoliciesIndexAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default PoliciesIndexAgGridHook;
